import {
  CoachingCaseFormat,
  CoachingChannel,
  CoachingDifficulty,
  CoachingOpeningSide,
} from '@euler/model/coaching';
import {
  CoachingCustomerMood,
  CoachingLiveArea,
  CoachingPracticeAudio,
  CoachingPracticeCustomer,
  CoachingPracticeEnd,
  CoachingPracticeLine,
  CoachingPracticeOpened,
  CoachingReplayOpened,
} from '@euler/model/coaching-practice';

/** What a scene plays against: a practice, or a replay of one moment. */
export type PracticeTarget =
  | { kind: 'practice'; attemptId: string }
  | { kind: 'replay'; attemptId: string; replayId: string };

/** A scene as the app shows it, whether a practice or a moment replay. */
export interface PracticeScene {
  target: PracticeTarget;
  title: string;
  channel: CoachingChannel;
  customer: CoachingPracticeCustomer;
  /** Bare dialogue: no mood, live scores, reactions or hints. */
  exam: boolean;
  format: CoachingCaseFormat;
  /** Null in a moment replay. */
  difficulty: CoachingDifficulty | null;
  /** A drill's goal line, at the top. */
  goal: string | null;
  /** The trainee opens: what they are about to do. */
  openingTask: string | null;
  maxLines: number;
  /** About how many lines the case runs to. */
  expectedLines: number;
  usedLines: number;
  transcript: CoachingPracticeLine[];
  mood: CoachingCustomerMood | null;
  areas: CoachingLiveArea[];
  end: CoachingPracticeEnd | null;
  /** A replay's better line, shown dimmed from the start. */
  fixedHint: string | null;
  /** The first customer line, spoken. */
  openingAudio: CoachingPracticeAudio[];
}

export function sceneFromPractice(
  opened: CoachingPracticeOpened,
): PracticeScene {
  return {
    target: { kind: 'practice', attemptId: opened.attemptId },
    title: opened.title,
    channel: opened.channel,
    customer: opened.customer,
    exam: opened.exam,
    format: opened.format,
    difficulty: opened.difficulty,
    goal: opened.drillGoal,
    openingTask:
      opened.opening.side === CoachingOpeningSide.Trainee
        ? opened.opening.task
        : null,
    maxLines: opened.turns.max,
    expectedLines: opened.turns.expected,
    usedLines: opened.turns.used,
    transcript: opened.transcript,
    mood: opened.mood,
    areas: opened.areas,
    end: opened.end,
    fixedHint: null,
    openingAudio: opened.openingAudio ?? [],
  };
}

export function sceneFromReplay(opened: CoachingReplayOpened): PracticeScene {
  return {
    target: {
      kind: 'replay',
      attemptId: opened.attemptId,
      replayId: opened.replayId,
    },
    title: opened.title,
    channel: opened.channel,
    customer: opened.customer,
    exam: false,
    format: CoachingCaseFormat.Full,
    difficulty: null,
    goal: null,
    openingTask: null,
    maxLines: opened.maxLines,
    expectedLines: opened.maxLines,
    usedLines: 0,
    transcript: opened.transcript,
    mood: opened.mood,
    areas: [],
    end: null,
    fixedHint: opened.betterLine,
    openingAudio: opened.customerAudio ?? [],
  };
}
